/**
 * 获取设备信息，用于 device-info 请求头
 * 格式: { "brand_model": "Redmi K80", "system_version": "Android 14" }
 */

export interface DeviceInfo {
  brand_model: string;
  system_version: string;
}

function parseFromUserAgent(ua: string): DeviceInfo {
  const uaLower = ua.toLowerCase();
  let brandModel = 'Unknown';
  let systemVersion = '';

  // Android: 解析 "(Linux; Android 14; Redmi K80 Build/...)" 或 "(Linux; Android 14; SM-G973F)"
  const androidMatch = ua.match(/Android\s+([\d.]+)/);
  if (androidMatch) {
    systemVersion = `Android ${androidMatch[1]}`;
    // 提取设备型号：Android 版本与 Build 或 ) 之间的内容
    const modelMatch = ua.match(/Android\s+[\d.]+\s*;\s*([^);]+?)(?:\s+Build)?\s*[)/]/);
    if (modelMatch) {
      const model = modelMatch[1].trim();
      // Chrome UA 精简后可能只显示 "K"
      brandModel = model.length <= 2 ? 'Android Device' : model;
    } else {
      brandModel = 'Android Device';
    }
  }

  // iOS
  const iosMatch = ua.match(/(?:iPhone|iPad|iPod).*?OS\s+([\d_]+)/i);
  if (iosMatch) {
    const version = iosMatch[1].replace(/_/g, '.');
    systemVersion = `iOS ${version}`;
    if (/iPad/i.test(ua)) {
      brandModel = 'iPad';
    } else if (/iPod/i.test(ua)) {
      brandModel = 'iPod Touch';
    } else {
      brandModel = 'iPhone';
    }
  }

  // 桌面端
  if (!systemVersion) {
    if (/windows/i.test(uaLower)) {
      const winMatch = ua.match(/Windows\s*(?:NT\s*)?([\d.]+)/i);
      systemVersion = winMatch ? `Windows ${winMatch[1]}` : 'Windows';
      brandModel = 'Desktop';
    } else if (/mac\s+os/i.test(uaLower) || /macintosh/i.test(uaLower)) {
      const macMatch = ua.match(/Mac\s*OS\s*X\s*[\s_]*([\d_]+)/i);
      systemVersion = macMatch ? `macOS ${macMatch[1].replace(/_/g, '.')}` : 'macOS';
      brandModel = 'Desktop';
    } else {
      systemVersion = 'Web';
      brandModel = 'Desktop';
    }
  }

  return { brand_model: brandModel, system_version: systemVersion };
}

let cachedDeviceInfo: DeviceInfo | null = null;

/**
 * 获取设备信息（从 User-Agent 解析，兼容 SSR）
 */
export function getDeviceInfo(): DeviceInfo {
  if (cachedDeviceInfo) {
    return cachedDeviceInfo;
  }

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  cachedDeviceInfo = parseFromUserAgent(ua);
  return cachedDeviceInfo;
}

/**
 * 获取 device-info 请求头的值（JSON 字符串）
 */
export function getDeviceInfoHeader(): string {
  const info = getDeviceInfo();
  return JSON.stringify(info);
}
